import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Box,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Select,
  Pagination,
  Alert,
  Chip,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import Header1 from "../Header/Header1";

const API_URL = process.env.REACT_APP_API_URL;

/* --------------------- ★ MODERN STYLED COMPONENTS ★ --------------------- */

const NeonCard = styled(Card)(() => ({
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.1)",
  transition: "0.25s ease",
  padding: "5px",
  "&:hover": {
    transform: "translateY(-6px)",
    borderColor: "#00eaff",
    boxShadow: "0 0 25px rgba(0, 191, 255, 0.4)",
  },
}));

const SearchField = styled(TextField)(() => ({
  input: {
    color: "#fff",
    background: "#0f0f0f",
    borderRadius: "8px",
    padding: "10px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#333" },
    "&:hover fieldset": { borderColor: "#777" },
    "&.Mui-focused fieldset": { borderColor: "#00eaff" },
  },
}));

/* ------------------------------------------------------------------------ */

function AdminContactUs({ userId, onLogout }) {
  const [contactMessages, setContactMessages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchRef = useRef(null);

  const fetchContactUsMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/get_contact_us_details/`);
      setContactMessages(res.data);
      setFiltered(res.data);
    } catch {
      setError("Failed to fetch contact messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactUsMessages();
  }, []);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, [loading]);

  /* ------------------- FILTERING ------------------- */
  useEffect(() => {
    const result = contactMessages.filter((m) =>
      `${m.name} ${m.email} ${m.contact} ${m.message}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFiltered(result);
    setCurrentPage(1);
  }, [search, contactMessages]);

  /* ------------------- PAGINATION ------------------- */
  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const paginatedMessages = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /* ------------------------------------------------------------------------ */

  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Container
        sx={{
          backgroundColor: "#000000ff",
          minHeight: "100vh",
          pt: 10,
          pb: 6,
          color: "#fff",
        }}
        maxWidth="lg"
      >
        {/* ---------------- TITLE ---------------- */}
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 4,
            letterSpacing: 1.5,
            background: "linear-gradient(90deg,#00eaff,#ff00ff)",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Admin — Contact Messages
        </Typography>

        {/* ---------------- Filters & Search ---------------- */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 4, gap: 2 }}
        >
          <Chip
            label={`Total: ${contactMessages.length}  |  Matching: ${filtered.length}`}
            sx={{
              background: "rgba(0,255,255,0.15)",
              color: "#00eaff",
              fontWeight: "bold",
              px: 2,
              py: 1,
            }}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            {/* Search */}
            <SearchField
              inputRef={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, contact, message..."
              size="small"
              sx={{ width: { xs: "100%", sm: "300px" } }}
            />

            {/* Pagination size */}
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              size="small"
              sx={{
                color: "#fff",
                background: "#0f0f0f",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#777" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00eaff",
                },
                minWidth: 90,
              }}
            >
              {[6, 12, 18].map((num) => (
                <MenuItem value={num} key={num}>
                  {num}/page
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* ---------------- Loading / Error ---------------- */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress sx={{ color: "#00eaff" }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        ) : filtered.length === 0 ? (
          <Typography
            variant="h6"
            align="center"
            sx={{ color: "#bbb", mt: 4 }}
          >
            No messages found.
          </Typography>
        ) : (
          <>
            {/* ---------------- Messages Grid ---------------- */}
            <Grid container spacing={4}>
              {paginatedMessages.map((msg) => (
                <Grid item xs={12} sm={6} md={4} key={msg.id}>
                  <NeonCard>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#00eaff",
                          fontSize: "1.2rem",
                        }}
                      >
                        {msg.name}
                      </Typography>

                      <Divider sx={{ backgroundColor: "#333", my: 1.5 }} />

                      <Typography sx={{ color: "#ccc", mb: 0.5 }}>
                        <strong>Email:</strong> {msg.email}
                      </Typography>
                      <Typography sx={{ color: "#ccc", mb: 0.5 }}>
                        <strong>Contact:</strong> {msg.contact}
                      </Typography>
                      <Typography sx={{ color: "#ccc", mb: 0.5 }}>
                        <strong>Message:</strong> {msg.message}
                      </Typography>
                    </CardContent>
                  </NeonCard>
                </Grid>
              ))}
            </Grid>

            {/* ---------------- Pagination ---------------- */}
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(e, val) => setCurrentPage(val)}
                  color="primary"
                  size="large"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#00eaff",
                    },
                    "& .Mui-selected": {
                      background:
                        "linear-gradient(90deg,#00eaff,#ff00ff) !important",
                      color: "#000 !important",
                      boxShadow: "0 0 10px #00eaff",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </div>
  );
}

export default AdminContactUs;
